import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MidpointsSection = ({ data, isVisible, onToggle }) => {
  const [selectedPlanet, setSelectedPlanet] = useState('all');

  if (!data || data?.length === 0) return null;

  const planets = ['all', ...new Set(data.flatMap(item => [item.planetA, item.planetB]))];
  
  const filteredData = selectedPlanet === 'all' 
    ? data 
    : data?.filter(item => item?.planetA === selectedPlanet || item?.planetB === selectedPlanet);

  return (
    <section id="midpoints" className="scroll-mt-20">
      <div className="bg-card border border-border rounded-lg shadow-brand-md overflow-hidden">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 md:px-6 py-4 hover:bg-muted transition-smooth"
          aria-expanded={isVisible}
        >
          <div className="flex items-center">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mr-3">
              <Icon name="GitBranch" size={20} color="var(--color-accent)" />
            </div>
            <div className="text-left">
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                จุดกึ่งกลาง
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground">Midpoints</p>
            </div>
          </div>
          <Icon 
            name={isVisible ? "ChevronUp" : "ChevronDown"} 
            size={24} 
            color="var(--color-muted-foreground)"
          />
        </button>

        {isVisible && (
          <div className="border-t border-border">
            {/* Filter Controls */}
            <div className="p-4 md:p-6 bg-muted/50 border-b border-border">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <label className="text-sm font-caption font-medium text-foreground">
                  กรองตามดาวเคราะห์:
                  <span className="block text-xs text-muted-foreground">Filter by Planet</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {planets?.map((planet) => (
                    <Button
                      key={planet}
                      variant={selectedPlanet === planet ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedPlanet(planet)}
                    >
                      {planet === 'all' ? 'ทั้งหมด (All)' : planet}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                      คู่ดาวเคราะห์
                      <span className="block text-xs text-muted-foreground normal-case">Planet Pair</span>
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                      องศาจุดกึ่งกลาง
                      <span className="block text-xs text-muted-foreground normal-case">Midpoint Degree</span>
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-caption font-medium text-foreground uppercase tracking-wide">
                      ค่าเบี่ยงเบน
                      <span className="block text-xs text-muted-foreground normal-case">Orb Deviation</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredData?.map((midpoint, index) => (
                    <tr key={index} className="hover:bg-muted/50 transition-smooth">
                      <td className="px-6 py-4 text-sm font-medium text-foreground">
                        {midpoint?.planetA} / {midpoint?.planetB}
                      </td>
                      <td className="px-6 py-4 text-sm data-text text-foreground">
                        {midpoint?.midpointDegree}°
                      </td>
                      <td className="px-6 py-4 text-sm data-text text-foreground">
                        {midpoint?.orbDeviation}°
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-border">
              {filteredData?.map((midpoint, index) => (
                <div key={index} className="p-4 hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-base font-medium text-foreground">
                        {midpoint?.planetA} / {midpoint?.planetB}
                      </h3>
                      <p className="text-sm text-muted-foreground">Planet Pair</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">องศาจุดกึ่งกลาง</p>
                      <p className="text-sm data-text text-foreground">{midpoint?.midpointDegree}°</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">ค่าเบี่ยงเบน</p>
                      <p className="text-sm data-text text-foreground">{midpoint?.orbDeviation}°</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredData?.length === 0 && (
              <div className="p-8 text-center">
                <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">ไม่พบข้อมูลสำหรับดาวเคราะห์ที่เลือก</p>
                <p className="text-xs text-muted-foreground">No data found for selected planet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default MidpointsSection;